import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import { IconSend, IconTrash, IconX } from '@tabler/icons-react'
import cssModule from './Dropzone.module.css'
import Spinner from '../../Components/Spinner'
import { getCompoundKey } from '../../Components/request-url-splitter'
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from './Dropzone.style'
import { requestGet, requestReply, requestRead } from '../../Api/Requests'
import { mergeUniqueFiles } from '../../utils/mergeUniqueFiles'
import { createPayloadFromFiles, formatPayload } from '../../utils/requestPayload'
import Thumbs from './Components/Thumbs'

function withPreview(file) {
  return Object.assign(file, {
    preview: URL.createObjectURL(file)
  })
}

export default function Previews(props) {
  const [currentRequest, setCurrentRequest] = useState(null)
  const [files, setFiles] = useState([])
  const [dropzoneOpen, setDropzoneOpen] = useState(true)
  const [sendingSelection, setSendingSelection] = useState(false)
  const [modalShow, setModalShow] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const [lightboxKey, setLightboxKey] = useState(0)
  const filesRef = useRef(files)
  filesRef.current = files

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/*',
    onDrop: acceptedFiles => {
      setFiles(prev => {
        if (prev.length === 0) {
          return acceptedFiles.map(withPreview)
        }
        return mergeUniqueFiles(prev, acceptedFiles, { mapIncoming: withPreview })
      })
      setDropzoneOpen(false)
    }
  })

  const compoundKey = getCompoundKey(window.location.pathname)

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [isDragAccept, isDragActive, isDragReject]);

  const removeFile = (index) => {
    const wasOpen = lightboxIndex >= 0
    const remaining = files.length - 1

    setFiles(prev => {
      if (index < 0 || index >= prev.length) return prev
      const next = [...prev]
      const [removed] = next.splice(index, 1)
      if (removed?.preview) URL.revokeObjectURL(removed.preview)
      return next
    })

    setLightboxIndex(current => {
      if (current < 0) return current
      if (remaining <= 0) return -1
      if (current > index) return current - 1
      if (current === index) return Math.min(index, remaining - 1)
      return current
    })

    // v2.x only applies `index` when the lightbox opens — remount to show the next slide
    if (wasOpen && remaining > 0) {
      setLightboxKey(key => key + 1)
    }
  }

  const deleteSelection = () => {
    if (!window.confirm('Θέλεις σίγουρα να διαγράψεις την επιλογή σου;')) return
    files.forEach(file => URL.revokeObjectURL(file.preview))
    setFiles([])
    setLightboxIndex(-1)
    setDropzoneOpen(true)
  }

  const handleSend = async () => {
    setSendingSelection(true)
    await requestReply({
      key: compoundKey,
      payload: createPayloadFromFiles(files),
    })
      .then(() => {
        setSendingSelection(false)
        setModalShow(true)
      })
  }

  // if request.opened_at is null, mark request as viewed...
  if (currentRequest) {
    if (!currentRequest.opened_at) {
      requestRead({
        key: compoundKey,
        opened_at: Date.now()
      })
    }
  }

  useEffect(() => {
    async function getRequestData(key) {
      const requestData = await requestGet({ key })
      setCurrentRequest(requestData.data)
    }
    getRequestData(compoundKey)
  }, [compoundKey])

  useEffect(() => {
    return () => {
      filesRef.current.forEach(file => URL.revokeObjectURL(file.preview))
    }
  }, [])

  useEffect(() => {
    if (files.length === 0) setDropzoneOpen(true)
  }, [files.length])

  if (!currentRequest) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8 text-mauve-600" />
      </div>
    )
  }

  if (currentRequest.status < 2) {
    return (
      <div className="text-center text-mauve-800">
        <h3 className="mb-2 text-xl font-semibold">
          You don't have the permission to view this page.
        </h3>
        Contact your photographer if you believe this is a mistake.
      </div>
    )
  }

  return (
    <section className="container">
      <div className='mb-4'>
        {currentRequest ?
          <div>
            <h4 className='mb-1 text-lg font-semibold text-mauve-900'>{currentRequest.title}</h4>
          </div>
          :
          <Spinner className="text-mauve-600" />
        }
      </div>

      {dropzoneOpen ? (
        <div>
          {files.length > 0 && (
            <div className="mb-2">
              <button
                type="button"
                className="text-sm font-medium text-mauve-700 underline underline-offset-2 transition hover:text-mauve-900"
                onClick={() => setDropzoneOpen(false)}
              >
                Ακύρωση προσθήκης
              </button>
            </div>
          )}
          <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <p className='text-center text-mauve-700'>
              {files.length > 0
                ? 'Σύρε εδώ επιπλέον φωτογραφίες ή κάνε κλικ για προσθήκη.'
                : 'Σύρε εδώ τις φωτογραφίες που έχεις επιλέξει ή κάνε κλικ για επιλογή.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="py-2">
          <button
            type="button"
            className="text-sm font-medium text-mauve-700 underline underline-offset-2 transition hover:text-mauve-900"
            onClick={() => setDropzoneOpen(true)}
          >
            Προσθήκη περισσότερων
          </button>
        </div>
      )}
      <div className={`${cssModule.toolbar} flex flex-wrap items-center gap-4`}>
        <div className="w-full md:w-auto md:flex-1">
          <span className="text-mauve-700">Επιλεγμένες εικόνες: {files.length}</span>
        </div>

        {files.length > 0 &&
          <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-2 bg-mauve-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-mauve-700 disabled:opacity-60"
              onClick={handleSend}
              disabled={sendingSelection}
            >
              {sendingSelection && <Spinner className="text-white" />}
              Αποστολή επιλογής
              <IconSend className="size-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center border border-red-500 px-3 py-2 text-red-600 transition hover:bg-red-50"
              onClick={deleteSelection}
              aria-label="Delete selection"
            >
              <IconTrash className="size-4" />
            </button>
          </div>
        }
      </div>

      {
        false ?
          <textarea
            className="mt-4 w-full rounded-sm border border-mauve-300 bg-mauve-50 p-3 text-sm text-mauve-900"
            rows={4}
            readOnly
            value={formatPayload(createPayloadFromFiles(files))}
          /> : ''
      }

      <aside className={cssModule.thumbsContainer}>
        <Thumbs
          files={files}
          onThumbClick={setLightboxIndex}
          onRemove={removeFile}
        />
      </aside>

      <Lightbox
        key={lightboxKey}
        open={lightboxIndex >= 0}
        index={Math.max(lightboxIndex, 0)}
        close={() => setLightboxIndex(-1)}
        slides={files.map(file => ({ src: file.preview }))}
        plugins={[Zoom]}
        on={{ view: (index) => setLightboxIndex(index) }}
        toolbar={{
          buttons: [
            <button
              key="delete"
              type="button"
              className="yarl__button"
              aria-label="Αφαίρεση"
              title="Αφαίρεση"
              onClick={() => removeFile(lightboxIndex)}
            >
              <IconTrash size={18} />
            </button>,
            'close',
          ],
        }}
      />

      {modalShow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close modal"
            onClick={() => setModalShow(false)}
          />
          <div
            role="dialog"
            aria-labelledby="contained-modal-title-vcenter"
            className="relative z-10 w-full max-w-md rounded-sm bg-white shadow-lg"
          >
            <div className="flex items-center justify-between border-b border-mauve-200 px-4 py-3">
              <h2 id="contained-modal-title-vcenter" className="w-full text-center text-lg font-semibold text-mauve-900">
                Όλα έτοιμα!
              </h2>
              <button
                type="button"
                className="absolute right-3 top-3 text-mauve-500 hover:text-mauve-800"
                aria-label="Close"
                onClick={() => setModalShow(false)}
              >
                <IconX className="size-4" />
              </button>
            </div>
            <div className="px-4 py-4">
              <img className="w-full" src='\images\mail-download-white.gif' alt='Envelope flying to its destination' />
              <p className="mt-4 text-center text-mauve-800">Η επιλογή σου έρχεται!</p>
              <p className="text-center text-mauve-700">Μπορείς να κλείσεις αυτό το παράθυρο και να περιμένεις νέα μου.</p>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
